import { lazy, Suspense, useMemo, useState } from "react";
import {
  AppstoreOutlined,
  ContactsOutlined,
  DashboardOutlined,
  FileTextOutlined,
  HomeOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuOutlined,
  NotificationOutlined,
  PictureOutlined,
  ProductOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Grid,
  Layout,
  Menu,
  Segmented,
  Spin,
  Typography,
} from "antd";
import type { MenuProps } from "antd";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./auth";
import { LoginPage } from "./pages/LoginPage";
import {
  setContentLanguage,
  useContentLanguage,
  type ContentLanguage,
} from "./i18n/content-language";

const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const PageConfigPage = lazy(() =>
  import("./pages/PageConfigPage").then((module) => ({
    default: module.PageConfigPage,
  })),
);
const ArticleListPage = lazy(() =>
  import("./pages/ArticlePages").then((module) => ({
    default: module.ArticleListPage,
  })),
);
const ArticleEditorPage = lazy(() =>
  import("./pages/ArticlePages").then((module) => ({
    default: module.ArticleEditorPage,
  })),
);
const ProductListPage = lazy(() =>
  import("./pages/ProductPages").then((module) => ({
    default: module.ProductListPage,
  })),
);
const ProductEditorPage = lazy(() =>
  import("./pages/ProductPages").then((module) => ({
    default: module.ProductEditorPage,
  })),
);
const ProductDetailConfigPage = lazy(() =>
  import("./pages/ProductDetailConfigPage").then((module) => ({
    default: module.ProductDetailConfigPage,
  })),
);
const CategoryPage = lazy(() =>
  import("./pages/CategoryPage").then((module) => ({
    default: module.CategoryPage,
  })),
);
const MediaPage = lazy(() =>
  import("./pages/MediaPage").then((module) => ({
    default: module.MediaPage,
  })),
);
const InquiryPage = lazy(() =>
  import("./pages/InquiryPage").then((module) => ({
    default: module.InquiryPage,
  })),
);

const { Header, Content, Sider } = Layout;

const menuItems: MenuProps["items"] = [
  { key: "/", icon: <DashboardOutlined />, label: "控制台" },
  {
    key: "pages",
    icon: <AppstoreOutlined />,
    label: "页面配置",
    children: [
      { key: "/pages/site", icon: <SettingOutlined />, label: "全站配置" },
      { key: "/pages/home", icon: <HomeOutlined />, label: "首页" },
      { key: "/pages/contact", icon: <ContactsOutlined />, label: "Contact" },
      { key: "/pages/about", icon: <TeamOutlined />, label: "About" },
      { key: "/pages/blog", icon: <FileTextOutlined />, label: "Blog 列表页" },
      {
        key: "/pages/news",
        icon: <NotificationOutlined />,
        label: "News 列表页",
      },
      {
        key: "/pages/products",
        icon: <ProductOutlined />,
        label: "Products 列表页",
      },
      {
        key: "/pages/privacy-policy",
        icon: <SafetyCertificateOutlined />,
        label: "隐私与法律声明",
      },
    ],
  },
  {
    type: "group",
    label: "内容管理",
    children: [
      { key: "/products", icon: <ProductOutlined />, label: "产品管理" },
      {
        key: "/product-detail-config",
        icon: <QuestionCircleOutlined />,
        label: "产品详情 FAQ",
      },
      { key: "/categories", icon: <TagsOutlined />, label: "产品分类" },
      { key: "/news", icon: <NotificationOutlined />, label: "News 管理" },
      { key: "/blogs", icon: <FileTextOutlined />, label: "Blog 管理" },
      { key: "/inquiries", icon: <MailOutlined />, label: "询盘管理" },
      { key: "/media", icon: <PictureOutlined />, label: "媒体资源" },
    ],
  },
];

function selectedMenuKey(pathname: string) {
  if (pathname.startsWith("/pages/")) return pathname;
  if (pathname.startsWith("/products/")) return "/products";
  if (pathname.startsWith("/news/")) return "/news";
  if (pathname.startsWith("/blogs/")) return "/blogs";
  return pathname;
}

function Brand() {
  return (
    <div className="admin-brand">
      <span className="admin-brand-mark">K</span>
      <div>
        <strong>KABEL</strong>
        <small>CONTENT ADMIN</small>
      </div>
    </div>
  );
}

function Navigation({
  onNavigate,
}: {
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <Menu
      className="admin-menu"
      defaultOpenKeys={["pages"]}
      items={menuItems}
      mode="inline"
      onClick={({ key }) => {
        navigate(key);
        onNavigate?.();
      }}
      selectedKeys={[selectedMenuKey(location.pathname)]}
      theme="dark"
    />
  );
}

function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const mobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const contentLanguage = useContentLanguage();

  const accountItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "account",
        icon: <UserOutlined />,
        label: `管理员：${admin?.username ?? "admin"}`,
        disabled: true,
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        danger: true,
        label: "退出登录",
        onClick: async () => {
          await logout();
          navigate("/login", { replace: true });
        },
      },
    ],
    [admin?.username, logout, navigate],
  );

  return (
    <Layout className="admin-shell">
      {!mobile ? (
        <Sider className="admin-sider" width={260}>
          <Brand />
          <Navigation />
          <div className="sider-version">KABEL CMS · LOCAL</div>
        </Sider>
      ) : null}
      <Drawer
        className="mobile-nav"
        closable={false}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        placement="left"
        styles={{ body: { padding: 0, background: "#1f1a17" } }}
        size={280}
      >
        <Brand />
        <Navigation onNavigate={() => setDrawerOpen(false)} />
      </Drawer>
      <Layout>
        <Header className="admin-header">
          <div className="header-left">
            {mobile ? (
              <Button
                aria-label="打开导航"
                icon={<MenuOutlined />}
                onClick={() => setDrawerOpen(true)}
                type="text"
              />
            ) : null}
            <Typography.Text type="secondary">
              KABEL 官网内容中心
            </Typography.Text>
            <Segmented
              aria-label="内容语言"
              onChange={(value) =>
                setContentLanguage(value as ContentLanguage)
              }
              options={[
                { label: "English", value: "en" },
                { label: "中文", value: "cn" },
              ]}
              value={contentLanguage}
            />
          </div>
          <Dropdown menu={{ items: accountItems }} placement="bottomRight">
            <button className="account-trigger" type="button">
              <Avatar icon={<UserOutlined />} size={34} />
              <span>{admin?.username ?? "admin"}</span>
            </button>
          </Dropdown>
        </Header>
        <Content className="admin-content">
          <Suspense
            fallback={
              <div className="page-loading">
                <Spin size="large" />
              </div>
            }
          >
            <Outlet key={contentLanguage} />
          </Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}

function ProtectedRoute() {
  const { admin, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <Spin size="large" />
        <span>正在进入 KABEL Admin…</span>
      </div>
    );
  }
  if (!admin) return <Navigate replace to="/login" />;
  return <AdminLayout />;
}

function NotFound() {
  const navigate = useNavigate();
  return (
    <div className="not-found">
      <Typography.Title>404</Typography.Title>
      <Typography.Paragraph>没有找到这个后台页面。</Typography.Paragraph>
      <Button onClick={() => navigate("/")} type="primary">
        返回控制台
      </Button>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<LoginPage />} path="/login" />
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardPage />} index />
        <Route element={<PageConfigPage />} path="/pages/:key" />
        <Route element={<ArticleListPage kind="blog" />} path="/blogs" />
        <Route element={<ArticleEditorPage kind="blog" />} path="/blogs/new" />
        <Route
          element={<ArticleEditorPage kind="blog" />}
          path="/blogs/:id/edit"
        />
        <Route element={<ArticleListPage kind="news" />} path="/news" />
        <Route element={<ArticleEditorPage kind="news" />} path="/news/new" />
        <Route
          element={<ArticleEditorPage kind="news" />}
          path="/news/:id/edit"
        />
        <Route element={<ProductListPage />} path="/products" />
        <Route
          element={<ProductDetailConfigPage />}
          path="/product-detail-config"
        />
        <Route element={<ProductEditorPage />} path="/products/new" />
        <Route
          element={<ProductEditorPage />}
          path="/products/:id/edit"
        />
        <Route element={<CategoryPage />} path="/categories" />
        <Route element={<MediaPage />} path="/media" />
        <Route element={<InquiryPage />} path="/inquiries" />
        <Route element={<NotFound />} path="*" />
      </Route>
    </Routes>
  );
}
